import { Dispatch, SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { useMediaQuery } from '@mui/material';
import { mq } from 'src/themes';
import { usePalette, useTranslation } from 'src/hooks';
import { transientOptions } from 'src/utils';
import { Link, SvgIcon, Typography } from 'src/components/atoms';
import { FlexColumn } from 'src/components/layout';
import { useGetNavbarSections } from './useGetNavbarSections';

interface NavBarProps {
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

interface StyledNavbarProps {
  $doesNavigationFit: boolean;
}

const StyledNavbar = styled(
  'ul',
  transientOptions
)<StyledNavbarProps>(
  ({ $doesNavigationFit, theme }) => `
  overflow-y: ${$doesNavigationFit ? 'none' : 'auto'};
  height: ${$doesNavigationFit ? 'calc(100vh - 10rem)' : 'auto'};
  width: 21rem;
  display: flex;
  flex-direction: column;
  justify-content: ${$doesNavigationFit ? 'center' : 'inherit'};

  &::-webkit-scrollbar {
    width: 0.7rem;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.palette.background.paper};
  }

  ${mq.md}{
    &:last-child {
      padding-bottom: 5rem;
    }
  }
`
);

interface StyledListItemProps {
  $isActive: boolean;
  $isDisabled?: boolean;
}
const StyledListItem = styled(
  Link,
  transientOptions
)<StyledListItemProps>(
  ({ theme, $isActive, $isDisabled }) => `
  position: relative;
  display: grid;
  grid-template-columns: 2rem 1fr;
  align-items: center;
  padding: 0.7rem 0;
  padding-left: 2.5rem;
  background-color: ${$isActive ? theme.palette.background.paper : null};
  pointer-events: ${$isDisabled ? 'none' : 'initial'};

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background-color: ${$isActive ? theme.palette.primary.main : 'inherit'};
  }

  &:hover {
    background-color: ${theme.palette.background.paper};
  }
`
);

export const NavBar = ({ setIsOpen }: NavBarProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const matches = useMediaQuery('(min-height:1000px)');
  const { grey, text } = usePalette();
  const navSections = useGetNavbarSections();

  return (
    <StyledNavbar $doesNavigationFit={matches}>
      {navSections.map(section => (
        <FlexColumn key={section.name}>
          <Typography sx={{ padding: '1.25rem 2rem' }}>{t(`layout.navigation.${section.name}`)}</Typography>
          {section.items.map(navItem => (
            <StyledListItem
              key={navItem.text}
              to={navItem.to}
              $isActive={pathname === navItem.to}
              $isDisabled={navItem.disabled}
              underline='none'
              onClick={() => (setIsOpen ? setIsOpen(false) : null)}
            >
              <SvgIcon>{navItem.icon}</SvgIcon>
              <Typography sx={{ color: navItem.disabled ? grey[500] : text.primary }}>
                {t(`layout.navigation.item_${navItem.text}`)}
              </Typography>
            </StyledListItem>
          ))}
        </FlexColumn>
      ))}
    </StyledNavbar>
  );
};
